import keyBy from 'lodash/keyBy';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { useEffect, useState } from 'react';
import { MoveToFolderModal } from 'ui/indexPages/modals/MoveToFolderModal';
import { isSelectionContent, isSelectionFolder } from 'utils/contexts/selection';
import noOp from 'utils/noop';

import { useCampaignQueryContext } from '../campaignFolders/contexts/CampaignQueryContext';
import { useSelectionContext } from '../campaignFolders/contexts/SelectionContext';
import { CONTENT_ITEM_NAME } from '../constants';
import { CampaignId } from '../domainTypes';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { useMoveCampaignsAndFolders } from '../hooks/useMoveCampaignsAndFolders';
import { useTreeViewQuery } from '../hooks/useTreeViewQuery';
import { usePaginationContext } from 'utils/contexts/pagination';
import { shouldMoveToPreviousPage } from 'utils/shouldMoveToPreviousPage';

export const displayContent = {
  cancel: 'Cancel',
  modalHeader: 'Move',
  moveToRoot: 'Move to overview',
  newFolder: 'New folder',
  next: 'Move to folder',
};

export const MoveFolderModal = () => {
  const { folderId, rootFolderId, refetch, paginationInfo } = useCampaignQueryContext();
  const { setCurrentPage, currentPage } = usePaginationContext();
  const { active, clearAll } = useSelectionContext();
  const modals = useCampaignFolderModals();
  const [destinationName, setDestinationName] = useState('');

  const [moveCampaignsAndFolders] = useMoveCampaignsAndFolders({
    onComplete: () => {
      modals.move.toggle(false);
    },
    onError: noOp,
    onSuccess: (data) => {
      const numMovedContent = data?.moveCampaignContentsAndFolders.movedContent?.length;
      if (!numMovedContent) return;
      if (shouldMoveToPreviousPage({ ...paginationInfo, numMovedContent })) {
        setCurrentPage(currentPage - 1);
        return;
      }
      refetch();
    },
    destinationName,
    itemsById: keyBy(active, 'id'),
  });

  useEffect(() => {
    return () => {
      // when an item gets set through the actions, we need to clear it on the way out
      clearAll();
    };
  }, []);

  const folderIdsToMove = active.filter(isSelectionFolder).map((c) => c.id);
  const contentIdsToMove = active.filter(isSelectionContent).map((c) => c.id);

  const handleMoveToFolderConfirmation = (
    folderIds: FolderId[],
    contentIds: CampaignId[],
    destination: AncestorIdData & AncestorNameData,
  ) => {
    setDestinationName(destination.name === '__root__' ? 'Main' : destination.name);
    moveCampaignsAndFolders({
      variables: {
        contentIds,
        destinationId: destination.id === rootFolderId ? null : destination.id,
        folderIds,
      },
    });
  };

  return (
    <MoveToFolderModal
      contentIds={contentIdsToMove}
      contentItemName={CONTENT_ITEM_NAME}
      currentFolderId={folderId}
      folderIds={folderIdsToMove}
      onClose={() => modals.move.toggle(false)}
      onConfirm={handleMoveToFolderConfirmation}
      rootFolderId={rootFolderId}
      treeViewQuery={useTreeViewQuery}
    />
  );
};
